/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';

import { Button, TITLE_TEXT, TockDialog } from 'omakase';
import { Crop } from 'react-image-crop';
import { Formik, FormikErrors, FormikProps } from 'formik';
import { StoreType, useActions } from '@tock/shared/utils/connect';
import { appRequestInProgressSelector } from 'selectors/appSelector';
import { core } from '@tock/coretypes';
import { isDefined } from 'utils/arrayUtils';
import { patronOpenModalSelector } from 'selectors/patronSelectors';
import {
  preferenceOptionsMapSelector,
  preferenceSelectOptionGroupsSelector,
} from 'selectors/preferencesSelector';
import { useSelector } from 'react-redux';
import EditProfileTimeFormInteral, { EditProfileTimeFormFields } from 'forms/EditProfileTimeForm';
import LoadingComponent from '@tock/shared/components/LoadingComponent';
import confirm from 'utils/confirm';
import protos from 'utils/protos';

const { PatronProfile } = protos;

interface Props {
  patronImageUrl?: string;
  patronProfile?: core.PatronProfile;
  patronProfilePlace?: google.maps.places.AutocompletePrediction;
}

export interface ImageState {
  file: File;
  orientation?: number;
  crop?: Crop;
}

function updateProfile(
  values: EditProfileTimeFormFields,
  existingProfile: core.PatronProfile,
  updatePatronProfile: (
    patronProfile: core.PatronProfile,
    showToast?: boolean
  ) => Promise<ProtoResponse<any>>
): Promise<ProtoResponse<any>> {
  const editedPatronProfile: core.IPatronProfile = {
    ...existingProfile,
    patronCuisineIds: values.cuisines?.map((option) => option.id).filter(isDefined) || [],
    patronAversions: values.aversions,
    patronDietaryRestrictionIds:
      values.restrictions?.map((option) => option.id).filter(isDefined) || [],
    patronHospitalityPreferenceIds:
      values.hospitality?.map((option) => option.id).filter(isDefined) || [],
    day: values.days,
    patronDestinations: values.destinations,
  };
  if (values.place) {
    editedPatronProfile.place = JSON.stringify(values.place);
  } else {
    delete editedPatronProfile.place;
  }
  const updatedProfile = PatronProfile.fromObject(editedPatronProfile);

  return updatePatronProfile(updatedProfile);
}

const EditProfileModal = ({ patronImageUrl, patronProfile, patronProfilePlace }: Props) => {
  const { isOpen, isLoading } = useSelector(mapStateToProps);
  const {
    actions: {
      patron: patronActions,
      app: { updatePatronProfile, updatePatronProfileImage, fetchPatronProfile },
      toast: { showError },
    },
  } = useActions();

  const [uploadedPatronImage, setUploadedPatronImage] = useState<ImageState>();
  const [isFormDirty, setIsFormDirty] = useState(false);

  const { allPreferenceSelectOptions, allPreferencesByIdByType } = useSelector(
    (state: StoreType) => ({
      allPreferenceSelectOptions: preferenceSelectOptionGroupsSelector(state),
      allPreferencesByIdByType: preferenceOptionsMapSelector(state),
    })
  );

  const onClose = async () => {
    if (isFormDirty) {
      try {
        await confirm.show('You have unsaved changes. Are you sure you want to close?');
      } catch (_error) {
        return;
      }
    }
    patronActions.closeEditPatronProfileModal();
  };

  const handleSubmit = (values: EditProfileTimeFormFields) => {
    if (patronProfile) {
      updateProfile(values, patronProfile, updatePatronProfile)
        .then(handleSubmitPhoto)
        .then(fetchPatronProfile);
      patronActions.closeEditPatronProfileModal();
    }

    window.analytics.sendEvent('Profile', 'saved', 'Diner', null, {
      transport: 'beacon',
    });
  };

  const handleSubmitPhoto = () => {
    if (uploadedPatronImage) {
      return updatePatronProfileImage(
        uploadedPatronImage.file,
        uploadedPatronImage.orientation,
        uploadedPatronImage.crop
      ).catch(() => {
        showError('Unable to upload photo');
      });
    }
    return;
  };

  const initialValues = {
    imageField: patronImageUrl,
    cuisines:
      patronProfile?.patronCuisineIds
        .map((id) => allPreferencesByIdByType.cuisineOptions[id])
        .filter(isDefined) ?? [],
    restrictions:
      patronProfile?.patronDietaryRestrictionIds
        .map((id) => allPreferencesByIdByType.dietaryRestrictionOptions[id])
        .filter(isDefined) ?? [],
    aversions: patronProfile?.patronAversions ?? '',
    hospitality:
      patronProfile?.patronHospitalityPreferenceIds
        .map((id) => allPreferencesByIdByType.hospitalityOptions[id])
        .filter(isDefined) ?? [],
    days: patronProfile?.day ?? [],
    destinations: patronProfile?.patronDestinations ?? [],
    place: patronProfilePlace,
  };

  const validate = (values: EditProfileTimeFormFields) => {
    const errors: FormikErrors<EditProfileTimeFormFields> = {};
    if (values.aversions.length > 255) {
      errors.aversions = 'Note must be less than 255 characters';
    }
    return errors;
  };

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} validate={validate}>
      {(formikProps: FormikProps<EditProfileTimeFormFields>) => (
        <TockDialog
          type="fullScreen"
          headingConfig={{
            left: {
              configType: 'title',
              title: 'Edit your diner profile',
            },
            right: {
              configType: 'closeButton',
            },
          }}
          aria-labelledby={TITLE_TEXT}
          actionsContent={
            <Button
              fullWidth
              variant="primary"
              type="submit"
              size="large"
              onClick={() => formikProps.handleSubmit()}
            >
              Save
            </Button>
          }
          onClose={onClose}
          open={isOpen}
          showShadowWhenScrolled
          disablePadding
        >
          <div className="EditProfile-info-header">
            Build your profile to represent your preferences as a diner. Information you provide
            here will be visible to the restaurants you book.
          </div>
          <div className="EditProfileModal">
            <LoadingComponent isLoading={isLoading} containerClassName="EditProfile-loading">
              <EditProfileTimeFormInteral
                allPreferencesByIdByType={allPreferencesByIdByType}
                allPreferenceSelectOptions={allPreferenceSelectOptions}
                formikProps={formikProps}
                onImageChanged={setUploadedPatronImage}
                onShowError={showError}
                onSetIsDirty={setIsFormDirty}
              />
            </LoadingComponent>
          </div>
        </TockDialog>
      )}
    </Formik>
  );
};

function mapStateToProps(state: StoreType) {
  return {
    isOpen: patronOpenModalSelector(state) === 'EDIT_PROFILE',
    isLoading: appRequestInProgressSelector(state),
  };
}

export default EditProfileModal;
